/* eslint-disable @typescript-eslint/no-explicit-any */
import { IPG } from "@/types/summary";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { isCaretAtEnd, isCaretAtStart, placeCaretAtEnd } from "@/utils/range";
import "./index.less";
import fridayReporter from "@/utils/friday";
import { debounce } from "@common/utils/common";
import recordStore from "@/store/record.store";
import { handlePaste } from "./util";
import getClickedSpan from "@/utils/getClickSpan";
import { IBlurParaInfo } from "@/types/record";
import avPlayerStore from "@/store/avPlayer.store";

export interface EditItem {
  wi: string;
  newContent: string;
}

export interface EditRecordItemRef {
  focus: () => void;
  placeCaretAtEnd: () => void;
  getText: () => string;
}

export interface IDocItemProps {
  record: IPG;
  /** 生成中状态 */
  isGenerating?: boolean;
  /** 占位 */
  placeholder?: string;
  /** 是否为编辑态 */
  isEditing?: boolean;
  /** 样式 */
  className?: string;
  isPlaying?: boolean;
  underlineOnFocus?: boolean;
  hightlight?: (wiArr: string[], wi: string) => boolean;
  handleClickWordItem?: (bt: number) => void;
  handleDoubleClick?: (pi: string) => void;
  handleEdit: (
    pi: string,
    editItems: EditItem[],
    emptyWordAllowed: boolean
  ) => void;
  handleEnter?: (editItem: EditItem, cIndex: number, pi: string) => void;
  handleMerge?: (pi: string) => void;
  recomputeRowHeights?: () => void;
  onBlur?: (para: IBlurParaInfo)=> void;
}

export interface SpeakItemRef {
  focus: () => void;
  placeCaretAtEnd: () => void;
}

const EditRecordItem = forwardRef<EditRecordItemRef, IDocItemProps>(
  (props, ref) => {
    const {
      record,
      isGenerating,
      isEditing,
      className = "",
      handleClickWordItem,
      handleEdit,
      handleEnter,
      handleMerge,
      recomputeRowHeights,
      isPlaying,
      underlineOnFocus,
      onBlur
    } = props;
    const markMap = recordStore((state) => state.markMap);
    const playingSi = avPlayerStore((state) => state.playingSi);
    const [focusSenBt, setFocusSenBt] = useState(-1);
    const hasFocus = useRef(false);

    const wordNodes = useMemo(() => {
      let html = "";
      record.sc.forEach((sItem) => {
        sItem.words.forEach((word) => {
          html += `<span id="word" data-parent-id="${sItem.id}" data-word-id="${
            word.wi
          }" data-sent-bt="${sItem.bt}" data-sent-id="${
            sItem.si
          }" class="b-#5698FF ${(record.mark || word.mark) && "bg-#B2F5D4"}">${
            word.formatWord || word.word
          }</span>`;
        });
      });
      return html;
    }, [record, markMap]);

    useEffect(() => {
      divRef.current?.childNodes.forEach((word) => {
        const wordEle = word as HTMLSpanElement;
        const sentBt = Number(wordEle.dataset.sentBt);
        const sentId = wordEle.dataset.sentId;
        const isInPlayingTime = sentId === playingSi;
        const isInFocus = sentBt === focusSenBt;
        // 聚焦时需要添加下划线, 且聚焦时的优先级高于播放中
        if (underlineOnFocus && isInFocus) {
          if (wordEle.classList.contains("playing-active")) {
            wordEle.classList.remove("playing-active");
          }
          if (!wordEle.classList.contains("editing-active")) {
            wordEle.classList.add("editing-active");
          }
        }
        // 没有聚焦时，先去掉下划线，添加播放中的样式
        else{
          if (wordEle.classList.contains("editing-active")) {
            wordEle.classList.remove("editing-active");
          }
          if (
            !isPlaying ||
            (!isInPlayingTime && wordEle.classList.contains("playing-active"))
          ) {
            wordEle.classList.remove("playing-active");
          } else if (
            isInPlayingTime &&
            !wordEle.classList.contains("playing-active")
          ) {
            wordEle.classList.add("playing-active");
          }
        }
      });
    }, [underlineOnFocus, focusSenBt, isPlaying, playingSi]);

    const editItemsRef = useRef({} as Record<string, EditItem>);
    const divRef = useRef<HTMLDivElement>(null);

    // 统一提交
    const handleCommit = (emptyWordAllowed = false) => {
      const editItems: EditItem[] = [];
      for (const key in editItemsRef.current) {
        const editItem = editItemsRef.current[key];
        editItems.push(editItem);
      }
      handleEdit?.(record.pi, editItems, emptyWordAllowed);
    };

    const handleSplit = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const { startContainer, startOffset } = range;

        // 获取光标所在的span元素
        const span: any =
          startContainer.nodeType === Node.TEXT_NODE
            ? startContainer.parentElement
            : startContainer;

        const wi = span?.dataset.wordId;
        const newContent = span?.innerText;
        if (wi) {
          handleCommit(true);
          handleEnter?.({ wi, newContent }, startOffset, record.pi);
        } else if (isCaretAtEnd(divRef.current)) {
          const getLastSpan = (el: any) => {
            const spans = el.querySelectorAll("span");
            return spans[spans.length - 1];
          };
          const lastSpan = getLastSpan(divRef.current);
          const wi = lastSpan?.dataset.wordId;
          const newContent = lastSpan?.innerText;
          if (wi) {
            handleCommit(true);
            handleEnter?.({ wi, newContent }, newContent.length, record.pi);
          } else {
            handleCommit(true);
            handleEnter?.(
              { wi: Date.now().toString(), newContent: "" },
              0,
              record.pi
            );
          }
        }
      }
    };

    // 处理输入事件，合并，拆分
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Backspace") {
        const selection = window.getSelection();
        const selectedText = selection?.toString();
        const hasSelectedText = selectedText && selectedText.length > 0;

        // 如果有选中文本，遵循del键默认删除行为
        if (hasSelectedText) return;

        // 如果没有选中文本，且在段落开头，则更新该段修改，并向上合并段落
        if (isCaretAtStart(divRef.current)) {
          handleCommit(true);
          debounceReportEdit();
          handleMerge?.(record.pi);
        }
      }
      if (e.key === "Enter") {
        e.preventDefault();
        debounceReportEdit();
        handleSplit();
      }
    };

    const getInitialContent = useCallback(() => {
      editItemsRef.current = {};
      divRef.current?.querySelectorAll("span").forEach((span) => {
        const wi = span.dataset.wordId;
        if (wi) {
          editItemsRef.current[wi] = {
            wi: span.dataset.wordId!,
            newContent: span.innerText,
          };
        }
      });
    }, []);

    useEffect(() => {
      getInitialContent();
    }, [getInitialContent]);

    useEffect(() => {
      let dom = divRef.current;
      // 绑定点击事件
      const handleClickWord = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target) return;
        let targetSpan: HTMLElement;
        if (target.matches("span")) {
          targetSpan = target;
        } else if (target.matches("mark")) {
          targetSpan = target.parentNode as HTMLElement;
        } else {
          targetSpan = getClickedSpan() as HTMLElement;
        }
        if (targetSpan) {
          const bt = (targetSpan as HTMLElement).dataset.sentBt;
          if (bt) {
            handleClickWordItem?.(Number(bt));
            if (hasFocus.current) {
              setFocusSenBt(Number(bt));
            }
          }
        }
      };
      dom?.addEventListener("click", handleClickWord);

      // 监听内容变化
      let observer: MutationObserver;
      if (isEditing) {
        observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === "characterData") {
              const span = mutation.target.parentElement!;
              const wi = span.dataset.wordId;
              if (wi) {
                editItemsRef.current[wi] = { wi, newContent: span!.innerText };
              }
              recomputeRowHeights && recomputeRowHeights();
            } else if (mutation.type === "childList") {
              mutation.removedNodes?.forEach((node: any) => {
                if (
                  node.nodeType === Node.ELEMENT_NODE &&
                  node.nodeName?.toLowerCase() === "span"
                ) {
                  const wi = node.dataset.wordId;
                  if (wi) {
                    editItemsRef.current[wi] = { wi, newContent: "" };
                  }
                }
              });
              mutation.addedNodes?.forEach((node: any) => {
                if (
                  node.nodeType === Node.ELEMENT_NODE &&
                  node.nodeName?.toLowerCase() === "span"
                ) {
                  const wi = node.dataset.wordId;
                  if (wi) {
                    editItemsRef.current[wi] = {
                      wi,
                      newContent: node.innerText,
                    };
                  }
                }
                // 粘贴的文本节点
                else if (node.nodeType === Node.TEXT_NODE) {
                  const spanNode = node.parentNode;
                  const wi = spanNode?.dataset.wordId;
                  if (wi) {
                    editItemsRef.current[wi] = {
                      wi,
                      newContent: spanNode.innerText,
                    };
                  }
                }
              });
              recomputeRowHeights && recomputeRowHeights();
            }
          });
        });
        const config = { characterData: true, childList: true, subtree: true };
        if (divRef.current) {
          observer.observe(divRef.current, config);
          // 监听粘贴事件
          divRef.current.addEventListener("paste", handlePaste);
        }
      }
      return () => {
        observer?.disconnect();
        observer = null as any;
        dom?.removeEventListener("click", handleClickWord);
        divRef.current?.removeEventListener("paste", handlePaste);
        dom = null as any;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          divRef.current?.focus();
        },
        placeCaretAtEnd: () => {
          const span = divRef.current?.querySelector("span");
          if (span) {
            placeCaretAtEnd(span);
          }
        },
        getText: () => {
          return divRef.current?.innerText || "";
        },
      }),
      []
    );

    const debounceReportEdit = debounce(() => {
      if (location.pathname === "/record") {
        fridayReporter.pushData({
          eventCode: "RecordPage_Edit",
        });
      }
    }, 500);

    return (
      <div
        id="content"
        data-p-id={record.pi}
        className={`outline-none ${
          isGenerating ? "inline generating-text" : "w-full inline-block text"
        } ${className} break-all text-3.5 select-text`}
        ref={divRef}
        suppressContentEditableWarning
        contentEditable={isEditing}
        dangerouslySetInnerHTML={{ __html: wordNodes }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          handleCommit(false);
          hasFocus.current = false;
          setFocusSenBt(-1);
          onBlur && onBlur({...record, pContent: divRef.current?.innerText || ''});
        }}
        onFocus={() => {
          getInitialContent();
          hasFocus.current = true;
          recordStore.getState().setFocusedPara(record);
        }}
        onInput={(e: any) => {
          debounceReportEdit();
          const input = e.target.innerText;
          const spans = e.target.querySelectorAll("span");
          const spanLength = spans.length;
          const spanNode = spans[spanLength - 1];
          if (spanLength === 0) {
            // 插入span
            const span = document.createElement("span");
            span.className = "select-text text";
            span.innerText = input;
            span.dataset.wordId = Date.now().toString();
            e.target.innerText = "";
            e.target.appendChild(span);
            placeCaretAtEnd(span);
          } else if (spanLength >= 1 && spanNode?.innerText === "") {
            spanNode.innerText = input;
            e.target.innerHTML = "";
            e.target.appendChild(spanNode);
            placeCaretAtEnd(spanNode);
          }
        }}
      />
    );
  }
);

export default EditRecordItem;
